import React, { useMemo } from "react";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function parseVarianceSign(value) {
  if (value == null || value === "") return "neutral";
  const n = Number(value);
  if (Number.isFinite(n)) {
    if (n > 0) return "over";
    if (n < 0) return "under";
    return "neutral";
  }
  const s = String(value).trim();
  if (s.startsWith("+")) return "over";
  if (s.startsWith("-")) return "under";
  return "neutral";
}

/**
 * Spend by GL code — YTD actual bars with variance vs budget.
 */
const Report20SpendByGlCard = ({
  rows = [],
  lastClosedMonth = null,
  selectedGlCode = "",
}) => {
  const filtered = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const sel = selectedGlCode != null ? String(selectedGlCode).trim().toLowerCase() : "";
    if (!sel) return list;
    return list.filter(
      (r) => String(r.gl_code || "").trim().toLowerCase() === sel,
    );
  }, [rows, selectedGlCode]);

  const maxActual = useMemo(() => {
    let m = 0;
    filtered.forEach((r) => {
      const v = Math.abs(Number(r.ytd_actual) || 0);
      if (v > m) m = v;
    });
    return m || 1;
  }, [filtered]);

  const throughLabel =
    lastClosedMonth >= 1 && lastClosedMonth <= 12
      ? MONTH_NAMES[lastClosedMonth - 1]
      : null;

  if (!filtered.length) {
    return (
      <div className="mb-8 rounded-2xl border border-gray-100 bg-white shadow-md overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-slate-900">Spend by GL code</h3>
          <p className="text-xs text-slate-500 mt-1">
            {throughLabel
              ? `YTD actual through ${throughLabel} across all codes.`
              : "YTD actual across all codes."}{" "}
            Select a code above to focus the whole report on it.
          </p>
        </div>
        <p className="px-5 py-8 text-sm text-slate-500 text-center">
          No GL spend or budget for this year yet.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-8 rounded-2xl border border-gray-100 bg-white shadow-md overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-base font-bold text-slate-900">Spend by GL code</h3>
        <p className="text-xs text-slate-500 mt-1">
          {throughLabel
            ? `YTD actual through ${throughLabel} across all codes.`
            : "YTD actual across all codes."}{" "}
          Select a code above to focus the whole report on it.
        </p>
      </div>

      <ul className="divide-y divide-gray-100 px-5">
        {filtered.map((row) => {
          const actual = Math.abs(Number(row.ytd_actual) || 0);
          const pct = Math.min(100, Math.round((actual / maxActual) * 100));
          const sign = parseVarianceSign(row.variance);
          const varianceColor =
            sign === "over"
              ? "text-orange-600"
              : sign === "under"
                ? "text-green-600"
                : "text-slate-500";
          const varianceText = row.variance_fmt
            ? `${row.variance_fmt} vs bud`
            : "—";

          return (
            <li
              key={row.gl_code}
              className="flex flex-wrap items-center gap-3 py-3.5"
            >
              <div className="w-full sm:w-44 shrink-0">
                <span className="text-sm font-semibold text-slate-900 tabular-nums">
                  {row.gl_code}
                </span>
                {row.description ? (
                  <span className="text-sm text-slate-600"> {row.description}</span>
                ) : null}
              </div>

              <div className="flex-1 min-w-[120px] h-2.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-[var(--nav,#8c1a1a)] transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>

              <div className="w-28 text-right text-sm font-bold text-slate-900 tabular-nums">
                {row.ytd_actual_fmt || "—"}
              </div>

              {/* <div
                className={`w-36 text-right text-sm font-medium tabular-nums ${varianceColor}`}
              >
                {varianceText}
              </div> */}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Report20SpendByGlCard;
